import React from 'react';
import type { ReturnableBox } from '../../lib/debitNoteApi';
import { boxUnavailableReason, formatBoxWeight } from './debitNoteDisplay';

/**
 * The weighed boxes of one invoice line's batch, for a goods return (D44 #21,
 * VR-3). Catch-weight goods go back box by box: the boxes ticked ARE the
 * quantity, which the server works out from their weights and hands back in the
 * preview (`quantity`). Nothing is added up here.
 *
 * A box another return holds, or one being picked for a vessel, is shown but
 * cannot be ticked. One already ticked can still be unticked, so a draft saved
 * before the box was taken can let go of it.
 */
export const BoxPicker: React.FC<{
  lineNumber: number;
  unit: string;
  boxes: ReturnableBox[];
  /** The box seqs ticked, sorted. */
  selected: number[];
  disabled?: boolean;
  invalid?: boolean;
  /** What the server says the ticked boxes come to, in the billed unit. */
  quantity?: number | null;
  /**
   * What the boxes belong to, in their labels: "line 1" unless given. A client
   * credit note line can take boxes back from more than one batch (VR-4), and two
   * pickers on one line must not share a name.
   */
  subject?: string;
  onToggle: (seq: number, ticked: boolean) => void;
}> = ({ lineNumber, unit, boxes, selected, disabled = false, invalid = false, quantity, subject, onToggle }) => {
  const count = selected.length;
  const owner = subject || `line ${lineNumber}`;
  return (
    <div className="min-w-[11rem]">
      <div
        role="group"
        aria-label={`Boxes for ${owner}`}
        className={`max-h-44 space-y-1 overflow-y-auto rounded-md border px-2 py-1.5 ${
          invalid ? 'border-red-300' : 'border-slate-200'
        }`}
      >
        {boxes.map((box) => {
          const ticked = selected.includes(box.seq);
          const why = boxUnavailableReason(box);
          return (
            <label
              key={box.seq}
              className={`flex items-start gap-1.5 ${why ? 'text-slate-400' : 'text-slate-700'}`}
            >
              <input
                type="checkbox"
                aria-label={`Box ${box.seq} on ${owner}`}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                checked={ticked}
                disabled={disabled || (Boolean(why) && !ticked)}
                onChange={(e) => onToggle(box.seq, e.target.checked)}
              />
              <span>
                <span className="whitespace-nowrap tabular-nums">
                  Box {box.seq}
                  {box.code ? ` (${box.code})` : ''} ·{' '}
                  {box.quantity === null ? '--' : `${formatBoxWeight(box.quantity)} ${unit}`.trim()}
                </span>
                {why && <span className="block">{why}</span>}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-0.5 text-slate-500">
        {count === 0
          ? 'Tick the boxes that go back'
          : `${count} box${count === 1 ? '' : 'es'}${
              typeof quantity === 'number' ? ` · ${formatBoxWeight(quantity)} ${unit}`.trimEnd() : ''
            }`}
      </div>
    </div>
  );
};

export default BoxPicker;
